import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  fetchMediaAnalyzeBlobUrl,
  revokeBlobUrl,
} from "../../../utils/mediaAnalyzeMedia";

/**
 * @param {{ sources?: Array<{ fileName?: string, mimeType?: string, mediaUrl?: string|null }> }} props
 */
export default function MediaAnalyzeVideoSources({ sources = [] }) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const labelColor = useColorModeValue("gray.700", "gray.200");

  const playable = sources.filter((s) => s?.mediaUrl);

  if (!playable.length) return null;

  return (
    <VStack align="stretch" spacing={4} mb={4}>
      {playable.map((src, index) => (
        <VideoSourcePlayer
          key={`${src.mediaUrl}-${index}`}
          source={src}
          borderColor={borderColor}
          labelColor={labelColor}
        />
      ))}
    </VStack>
  );
}

function VideoSourcePlayer({ source, borderColor, labelColor }) {
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!source?.mediaUrl) return undefined;

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    (async () => {
      try {
        const url = await fetchMediaAnalyzeBlobUrl(source.mediaUrl);
        if (!cancelled) setVideoSrc(url);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setVideoSrc((prev) => {
        revokeBlobUrl(prev);
        return null;
      });
    };
  }, [source?.mediaUrl]);

  return (
    <Box
      borderRadius="xl"
      overflow="hidden"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Text fontSize="sm" fontWeight="semibold" px={4} py={2} color={labelColor}>
        {source.fileName || "Uploaded video"}
      </Text>
      <Box bg="black" minH="200px" display="flex" alignItems="center" justifyContent="center">
        {loading ? (
          <Spinner color="purple.300" />
        ) : failed ? (
          <Text fontSize="sm" color="gray.400" px={4} py={8} textAlign="center">
            Could not load video. Try refreshing or re-upload.
          </Text>
        ) : videoSrc ? (
          <Box as="video" src={videoSrc} controls w="100%" maxH="400px" />
        ) : null}
      </Box>
    </Box>
  );
}
